import { useEffect, useRef } from 'react';
import { useFetcher } from 'react-router';
import { Minus, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import { cn } from '@rocco/utils/cn';
import type { CartItem } from '~/modules/ecommerce/type/cart-item';
import type { ErrorResponse } from '~/modules/root/graphql/responses';
import { UPDATE_SHOPPING_CART_ITEM_API_ROUTE } from '~/routes/remix-api.ecommerce.update-shopping-cart-item';
import { fetcherIsDone } from '~/utils/fetcherHelper';
import { DRAWER_CTA } from './drawer-cta';

/**
 * The line's quantity, as a stepper, with a bin beside it: each step sets
 * the cart line's quantity on the server and asks the page for its cart
 * again, so the total in the button under it follows. The stepper never goes
 * below one; the bin sets zero, which takes the line out of the cart.
 */
const CartLineQuantity = ({ item }: { item: CartItem }) => {
    const fetcher = useFetcher<Record<string, unknown> | ErrorResponse>();
    // The step just asked for, shown at once rather than after the round trip.
    const pending = fetcher.formData
        ? Number(fetcher.formData.get('quantity'))
        : null;
    const quantity = pending ?? item.Quantity;
    // A step is a write against the cart's own count, not a delta, so two in
    // flight at once race: four quick taps send 2, 3, 4, 5 and the cart keeps
    // whichever lands last. One at a time, the way the Business Manager cart
    // serialises its own stepper behind a blocking spinner.
    const stepping = fetcher.state !== 'idle';
    // What the line may reach. A back-ordered variant has none — the supplier
    // makes more, so its stock number is a lead time rather than a limit — and
    // a variant that tracks no stock reports zero, which is not a ceiling
    // either. Without this the reader could step past the stock, the server
    // clamped it back in `updateCartItemStockAndPricing` without raising
    // anything, and the row returned to where it started with nothing said:
    // the same "the tap missed" reading the failure toast exists to prevent.
    const backOrdered =
        item.Variant?.AllowsBackOrder === true &&
        Boolean(item.Variant?.LeadTimeUnit);
    const stock = item.Variant?.AvailableStock ?? 0;
    const ceiling = backOrdered || stock <= 0 ? null : stock;
    const atCeiling = ceiling !== null && quantity >= ceiling;

    const setQuantity = (next: number) => {
        if (next < 0 || stepping) return;
        fetcher.submit(
            { cartItemId: String(item.ID), quantity: String(next) },
            { method: 'post', action: UPDATE_SHOPPING_CART_ITEM_API_ROUTE },
        );
    };

    // The fetcher holds its data after settling, so a re-render would report
    // the same failure twice.
    const reported = useRef<unknown>(null);
    useEffect(() => {
        if (!fetcherIsDone(fetcher) || !fetcher.data) return;
        if (reported.current === fetcher.data) return;
        reported.current = fetcher.data;
        if ('error' in fetcher.data) {
            // Swallowed, the optimistic number cleared with the form data and
            // the row snapped back to where it started — which reads as a tap
            // that missed, so the reader taps again. The server refuses for
            // reasons worth saying: the cart's total ceiling, a locked instant
            // purchase, an entitlement the variant no longer has.
            toast.error(
                typeof fetcher.data.message === 'string' && fetcher.data.message
                    ? fetcher.data.message
                    : 'Unable to update the cart',
            );
        }
        // React Router revalidates the page's loaders after a fetcher action
        // on its own — this route opts out only for `add-to-shopping-cart` —
        // so asking again here ran the product loader a second time for every
        // tap.
    }, [fetcher]);

    const stepClass =
        'inline-flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full text-[#181818] transition-colors hover:bg-black/5 disabled:cursor-default disabled:opacity-30';

    return (
        <span className="inline-flex shrink-0 items-center gap-1">
            <span className="inline-flex h-9 items-center rounded-full border border-black/15 px-0.5">
                <button
                    type="button"
                    aria-label="Decrease quantity"
                    disabled={stepping || quantity <= 1}
                    onClick={() => setQuantity(quantity - 1)}
                    className={stepClass}
                >
                    <Minus className="size-3.5" />
                </button>
                <span
                    aria-live="polite"
                    className="min-w-[1.5rem] text-center font-sans text-[0.875rem] text-[#181818] tabular-nums"
                >
                    {quantity}
                </span>
                <button
                    type="button"
                    aria-label="Increase quantity"
                    disabled={stepping || atCeiling}
                    title={atCeiling ? `Only ${ceiling} in stock` : undefined}
                    onClick={() => setQuantity(quantity + 1)}
                    className={stepClass}
                >
                    <Plus className="size-3.5" />
                </button>
            </span>
            <button
                type="button"
                aria-label="Remove from cart"
                disabled={stepping}
                onClick={() => setQuantity(0)}
                className={cn(stepClass, 'text-[#9b9b9b] hover:text-[#181818]')}
            >
                <Trash2 className="size-4" />
            </button>
        </span>
    );
};

/** Where the cart lives. A full load: the cart is its own page, not a route of this one. */
const CART_URL = '/shopping-cart';

interface DrawerCartPanelProps {
    /**
     * What the pane is: the cart, or the confirmation of an add. The product
     * drawer arrives here having just added something and says so; the header's
     * icon is only ever opening the cart.
     */
    title?: string;
    /**
     * The cart's lines and its total, from whichever surface knows them: the
     * product page's own summary, or the root loader's on every other page.
     */
    items: CartItem[];
    /**
     * The cart's item total. Named on the pill, because it is not the figure
     * `/shopping-cart` shows: that one is `CartTotal`, which carries delivery
     * and tax. Two dark pills one tap apart, both bare numbers, read as the
     * same money changing by itself.
     */
    subtotal?: string | null;
    /** Shuts the drawer — the pane's own quiet action. */
    onClose: () => void;
    /**
     * The line the add just created. The cart summary is loader data and only
     * catches up on revalidation, so the confirmation leads with this and falls
     * back to the summary — a reader who has just pressed the button should
     * never be shown an empty cart.
     */
    justAdded?: CartItem | null;
}

/**
 * The cart's contents, as a drawer pane.
 *
 * It replaces the header's little menu: that popup opens at the top of the
 * window, several hundred pixels from whatever filled it, over a page the
 * reader is still using. Shown here, on the product drawer's second pane after
 * an add and on its own from the header's cart icon.
 *
 * Its lines are passed in rather than read here — the product page has its own
 * cart summary and every other page has the root loader's, and this pane should
 * not care which it is looking at.
 */
export const DrawerCartPanel = ({
    title = 'Your Cart',
    items: cartItems,
    subtotal,
    justAdded,
    onClose,
}: DrawerCartPanelProps) => {
    const items =
        cartItems.length > 0 ? cartItems : justAdded ? [justAdded] : [];

    return (
        <div className="flex h-full min-h-0 flex-1 flex-col">
            <div className="flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-auto overscroll-contain">
                <div className="px-4 pt-[4.5rem]">
                    <p className="m-0 font-sans text-xl leading-[1.2] font-medium tracking-[-0.02em] text-[#181818]">
                        {title}
                    </p>

                    <ul className="m-0 mt-5 flex list-none flex-col gap-[1rem] p-0">
                        {items.map((item) => (
                            <li
                                key={item.ID}
                                className="flex items-center gap-[0.75rem]"
                            >
                                {item.Variant?.Thumbnail?.Desktop ? (
                                    <img
                                        src={item.Variant.Thumbnail.Desktop}
                                        alt=""
                                        className="size-[3.5rem] shrink-0 rounded-[0.75rem] bg-[#f5f5f7] object-cover"
                                    />
                                ) : null}
                                <span className="min-w-0 flex-1">
                                    <span className="block font-sans text-[0.875rem] leading-[1.3] text-[#181818]">
                                        {item.Variant?.Product?.Title}
                                    </span>
                                    <span className="mt-[0.125rem] block font-sans text-[0.8125rem] leading-[1.3] text-[#9b9b9b]">
                                        {item.Price?.ShortFormat}
                                        {item.Sample ? ' · Sample' : null}
                                    </span>
                                </span>
                                <CartLineQuantity item={item} />
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            {/* The same pair the product side closes on: the total rides in
                the dark button, as the price rides in Add to Cart, with the
                quiet one under it. */}
            <div className="flex shrink-0 flex-col gap-3 border-t border-black/10 px-4 pt-4 pb-6">
                <a
                    href={CART_URL}
                    className={cn(DRAWER_CTA('filled-light'), 'no-underline')}
                >
                    {subtotal
                        ? `Subtotal ${subtotal} — View Cart`
                        : 'View Cart'}
                </a>
                <button
                    type="button"
                    onClick={onClose}
                    className={DRAWER_CTA('white-lifted')}
                >
                    Close
                </button>
            </div>
        </div>
    );
};
